import { skipToken } from "@reduxjs/toolkit/query";
import { automationArtifactIdSchema } from "@repo/app-types/entities/automationArtifact";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import type { FC } from "react";
import { Navigate, useParams } from "react-router";
import { useMyAutomationResultLocationQuery } from "../../api/entities/automationArtifact.js";
import { appPaths } from "../../app/appPaths.js";
import { selectTeammate } from "../../features/teammate/teammateSlice.js";
import { useAppSelector } from "../../hooks/hook.js";

/**
 * The Team Member counterpart of {@link AutomationResultRedirectPage}: the same
 * playbook-free `/automation-results/:resultId` link a teammate receives by email or
 * Slack/Teams, resolved through the recipient-scoped endpoint and routed to
 * their own focused Notifications view rather than the editor report page they
 * can't access. Falls back to home when the id is malformed, the result isn't
 * one delivered to this teammate, or its automation has no home playbook.
 */
export const MyAutomationResultRedirectPage: FC = () => {
  const { resultId } = useParams();
  const currentTeammate = useAppSelector(selectTeammate);
  const parsedResultId = resultId
    ? automationArtifactIdSchema.safeParse(resultId)
    : undefined;

  const { data: location, isLoading } = useMyAutomationResultLocationQuery(
    parsedResultId?.success ? parsedResultId.data : skipToken,
  );

  if (!parsedResultId?.success) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!currentTeammate || !location || !location.playbook_id) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  return (
    <Navigate
      replace
      to={appPaths.teammateAutomationResult.build({
        playbookId: location.playbook_id,
        teammateId: currentTeammate.id,
        artifactId: parsedResultId.data,
      })}
    />
  );
};
